'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocBeforeAfter,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const applyUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ name: 'Alice', age: 30 }, null, 2));
    const patchEncoded = encodeBase64(JSON.stringify([
        { op: 'replace', path: '/age', value: 31 },
        { op: 'add', path: '/active', value: true },
    ], null, 2));
    return `/?old=${oldEncoded}&patch=${patchEncoded}&mode=apply`;
})();

const mistakes = [
    {
        mistake: 'Applying a patch you have not validated',
        fix: 'A patch is code: it can delete data. Run it against a copy first, or open it in the apply tool and inspect the result before it touches production data.',
    },
    {
        mistake: 'Assuming a failed patch changed nothing',
        fix: 'The RFC stops evaluation at the first failed operation, but whether earlier operations roll back depends on the library. If all-or-nothing matters to you, put a test operation first or apply to a copy.',
    },
    {
        mistake: 'Feeding the patch itself as the target document',
        fix: 'A classic copy-paste bug: the target is the document you want to change, the patch is the array of operations. Applying a patch to itself fails immediately.',
    },
    {
        mistake: 'Ignoring type mismatches in test operations',
        fix: 'test compares exactly: 1 and "1" are different values. A test that "should pass" and does not is usually a string-versus-number issue.',
    },
];

export default function HowToApplyJsonPatchPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>How to Apply a JSON Patch</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>How-To</DocEyebrow>
                        <DocTitle>How to Apply a JSON Patch</DocTitle>
                        <DocSubtitle>
                            You already have an RFC 6902 patch - from an API, a colleague, or a generator - and want to
                            execute it against a JSON document. Here is what applying means, how to do it safely, and
                            the same operation in the browser, JavaScript and Python.
                        </DocSubtitle>

                        <DocAnswer>
                            Applying a JSON Patch means executing its operations <strong>in order</strong> against the
                            target document. Each operation sees the result of the previous one, and evaluation stops
                            at the first operation that fails. Paste the document and the patch into the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>apply tool</Link> to see the result
                            instantly.
                        </DocAnswer>

                        <DocSection>A minimal example</DocSection>
                        <DocCode label="Patch">{`[
  { "op": "replace", "path": "/age", "value": 31 },
  { "op": "add", "path": "/active", "value": true }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{
  "name": "Alice",
  "age": 30
}`}
                            after={`{
  "name": "Alice",
  "age": 31,
  "active": true
}`}
                        />

                        <DocSection>The semantics that matter</DocSection>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><strong>Sequential.</strong> Operations run top to bottom. A path that is valid when you wrote the patch can be invalid by the time its operation runs - array indexes especially (see <Link href="/docs/json-patch-array" style={{ color: 'var(--accent)' }}>index shifting</Link>).</li>
                            <li><strong>Stop on first error.</strong> Per RFC 6902, evaluation terminates with an error as soon as one operation fails. Whether the earlier operations are rolled back is up to the library - many build the result separately, but do not rely on it without checking.</li>
                            <li><strong>Exact paths.</strong> Every <code>path</code> and <code>from</code> is a <Link href="/docs/json-pointer" style={{ color: 'var(--accent)' }}>JSON Pointer</Link>: keys match exactly, array indexes are zero-based.</li>
                        </Box>

                        <DocSection>Validate before you apply</DocSection>
                        <DocText>
                            Two habits prevent almost every bad application:
                        </DocText>
                        <DocText>
                            1. <strong>Guard assumptions with test.</strong> A{' '}
                            <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test operation</Link> at
                            the top of the patch fails the whole thing when the document is not in the state you
                            expect - optimistic concurrency for free:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "test", "path": "/age", "value": 30 },
  { "op": "replace", "path": "/age", "value": 31 }
]`}</DocCode>
                        <DocText>
                            2. <strong>Dry-run on a copy.</strong> The verify mode in this tool applies the patch and
                            checks the result against your expected document, with human-readable errors when an
                            operation fails. For failures and their fixes, see{' '}
                            <Link href="/docs/json-patch-errors" style={{ color: 'var(--accent)' }}>JSON Patch errors</Link>.
                        </DocText>

                        <DocSection>Applying in code</DocSection>
                        <DocCode label="JavaScript (fast-json-patch)">{`import { applyPatch } from 'fast-json-patch';

const result = applyPatch(document, patch).newDocument;`}</DocCode>
                        <DocCode label="Python (jsonpatch)">{`import jsonpatch

result = jsonpatch.apply_patch(document, patch)`}</DocCode>
                        <DocText>
                            More detail in the language guides:{' '}
                            <Link href="/guides/json-patch-in-javascript" style={{ color: 'var(--accent)' }}>JSON Patch in JavaScript</Link> and{' '}
                            <Link href="/guides/json-patch-in-python" style={{ color: 'var(--accent)' }}>JSON Patch in Python</Link>.
                        </DocText>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Reference</DocSection>
                        <DocText>
                            Operation semantics are in{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 4</a>,
                            and the error-termination rule in{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-5" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>section 5</a>.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                Apply this patch yourself
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Open the apply tool with the document and patch above preloaded - edit the patch and watch the result update.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                href={applyUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the apply tool with this example
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/how-to-generate-json-patch', title: 'How to generate a JSON Patch', description: 'Produce the patch from a before/after pair' },
                            { href: '/docs/json-patch-errors', title: 'JSON Patch errors', description: 'Every failure mode and its fix' },
                            { href: '/operations/test', title: 'Test operation', description: 'Preconditions that fail the patch safely' },
                            { href: '/docs/json-pointer', title: 'JSON Pointer', description: 'The path syntax every operation uses' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
